import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Channel } from '@player/domain';
import type { FocusPort } from '@player/ports';
import { ChannelLogo } from '../components/ChannelLogo.js';
import { FONT_STACK, colors, layout } from '../theme.js';
import { channelColorOf } from '../channelColors.js';
import { useTranslator } from '../i18n/I18nProvider.js';

/**
 * I canali preferiti: **solo quelli**, e senza un tetto.
 *
 * ## Cos'era, e perché non reggeva
 *
 * Era la schermata del prototipo: una griglia di **tutti** i canali, i preferiti a piena opacità e
 * gli altri smorzati, con OK che inverte. Sui sedici canali di esempio funziona; su un catalogo vero
 * è una griglia di dodicimila caselle in cui bisogna **trovare** quella da accendere, senza ricerca e
 * senza ordinamento — cioè uno strumento che non si può usare proprio quando servirebbe.
 *
 * Il preferito si aggiunge **dove si incontra il canale**: dalla stella sulla riga della guida, e dal
 * lettore mentre lo si guarda. Sono i due posti in cui il canale è già davanti agli occhi, e non c'è
 * niente da cercare. Questa schermata smette di essere un pannello di scelta e diventa quel che il
 * nome promette: l'elenco dei preferiti.
 *
 * ## Qui OK apre, non toglie
 *
 * È la conseguenza: in un elenco di soli preferiti, l'azione ovvia su una casella è **guardarla**. La
 * stella in alto a destra toglie — ed è la stessa stella, nello stesso posto, della riga della guida:
 * due gesti identici per la stessa cosa in due schermate diverse.
 *
 * ## Nessun tetto
 *
 * Non c'è un numero massimo e non c'è una pagina: la griglia scorre. Un limite qui sarebbe arbitrario
 * — non c'è niente che costi, l'elenco è una manciata di identificativi in `localStorage` — e si
 * scoprirebbe soltanto sbattendoci contro.
 */

export interface FavoritesScreenProps {
  readonly focusPort: FocusPort;
  readonly channels: ReadonlyArray<Channel>;
  readonly favorites: ReadonlyArray<string>;
  /** Toglie un canale dai preferiti. Aggiungere si fa dalla guida o dal lettore. */
  readonly onRemove: (channelId: string) => void;
  /** Apre il canale: è l'azione ovvia su una casella di un elenco di preferiti. */
  readonly onOpen: (channelId: string) => void;
  readonly onExit: () => void;
  /** Quante colonne. Fuori dalle prove lo decide il disegno; qui si può fissare. */
  readonly columns?: number;
}

const DEFAULT_COLUMNS = layout.favoriteColumns;

export function FavoritesScreen({
  focusPort,
  channels,
  favorites,
  onRemove,
  onOpen,
  onExit,
  columns = DEFAULT_COLUMNS,
}: FavoritesScreenProps): ReactNode {
  const translator = useTranslator();
  const [cursor, setCursor] = useState(0);
  /** Se il fuoco è sulla stella invece che sulla casella. Due bersagli, come nella guida. */
  const [onStar, setOnStar] = useState(false);

  /*
    L'elenco è **l'ordine dei preferiti**, non l'ordine del catalogo.

    Chi ha aggiunto un canale se lo aspetta dove l'ha messo. Riordinarli per numero li rimescolerebbe
    a ogni aggiunta, e la casella che si cerca non sarebbe mai dove era l'ultima volta.
  */
  const byId = new Map(channels.map((channel) => [channel.id, channel]));
  const chosen: ReadonlyArray<Channel> = favorites
    .map((id) => byId.get(id))
    .filter((channel): channel is Channel => channel !== undefined);

  const handlers = useRef({ onRemove, onOpen, onExit });
  useEffect(() => {
    handlers.current = { onRemove, onOpen, onExit };
  }, [onRemove, onOpen, onExit]);

  /*
    Il cursore si riporta dentro i limiti quando l'elenco si accorcia.

    Togliendo l'ultimo preferito il cursore punterebbe a una casella che non c'è: premere OK non
    farebbe niente, che è il modo peggiore di rompersi — sembra un telecomando scarico.
  */
  useEffect(() => {
    setCursor((at) => (chosen.length === 0 ? 0 : Math.min(at, chosen.length - 1)));
  }, [chosen.length]);

  const move = useCallback(
    (delta: number) => {
      setCursor((current) => {
        const next = current + delta;
        // Ai bordi si **resta**, non si avvolge: su una griglia il fuoco che salta dall'ultima
        // casella alla prima è imprevedibile.
        if (next < 0 || next >= chosen.length) return current;
        return next;
      });
    },
    [chosen.length],
  );

  useEffect(() => {
    return focusPort.subscribe((key) => {
      if (key.kind === 'back') {
        handlers.current.onExit();
        return true;
      }
      if (key.kind === 'select') {
        const channel = chosen[cursor];
        if (!channel) return true;
        if (onStar) handlers.current.onRemove(channel.id);
        else handlers.current.onOpen(channel.id);
        return true;
      }
      if (key.kind !== 'direction') return false;
      /*
        Su e giù muovono di una riga; destra e sinistra di una casella — **tranne** che la prima
        pressione verso destra su una casella porta sulla sua stella, e la prima verso sinistra
        torna indietro sulla casella.

        È la stessa grammatica della riga della guida, dove il canale e la sua stella sono due
        bersagli affiancati. Un gesto solo per due azioni diverse obbligherebbe a inventare un tasto
        che il telecomando non ha.
      */
      if (key.direction === 'right') {
        if (!onStar) setOnStar(true);
        else {
          setOnStar(false);
          move(1);
        }
      }
      if (key.direction === 'left') {
        if (onStar) setOnStar(false);
        else move(-1);
      }
      if (key.direction === 'up') {
        setOnStar(false);
        move(-columns);
      }
      if (key.direction === 'down') {
        setOnStar(false);
        move(columns);
      }
      return true;
    });
  }, [focusPort, chosen, cursor, columns, move, onStar]);

  return (
    <div
      data-favorites="1"
      style={{
        // Le misure del prototipo: respiro 72 su tre lati, 80 in basso, scorrimento dentro la colonna.
        flex: 1,
        minWidth: 0,
        overflowY: 'auto',
        scrollbarWidth: 'none',
        padding: '72px 72px 80px',
        display: 'flex',
        flexDirection: 'column',
        gap: 40,
        boxSizing: 'border-box',
      }}
    >
      <header style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h1 style={{ font: `800 64px ${FONT_STACK}`, letterSpacing: '-.02em', margin: 0 }}>
          {translator.t('favorites.title')}
        </h1>
        {/*
          Il conto, in ambra: quanti sono, senza un «su quanti».

          Il totale del catalogo non c'entra più niente con questa schermata — non è più una scelta
          fra dodicimila, è un elenco — e «3 / 12000» avrebbe solo fatto sembrare tre un numero basso.
        */}
        {chosen.length > 0 && (
          <span data-favorites-count="1" style={{ font: `700 28px ${FONT_STACK}`, color: colors.accent }}>
            {translator.t('favorites.count', { count: chosen.length })}
          </span>
        )}
      </header>

      {chosen.length === 0 ? (
        /*
          Vuoto, e **si dice dove si aggiungono**.

          Non è la didascalia di un gesto invisibile — quella era da togliere, ed è stata tolta: è
          l'unica informazione che questa schermata può dare quando non ha niente da mostrare. Senza,
          resterebbe una pagina bianca che sembra rotta.
        */
        <p data-favorites-empty="1" style={{ font: `500 25px/1.4 ${FONT_STACK}`, color: colors.textDim, margin: 0 }}>
          {translator.t('favorites.empty')}
        </p>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, minmax(0,1fr))`,
            gap: 24,
            alignContent: 'start',
          }}
        >
          {chosen.map((channel, index) => {
            const focused = index === cursor;
            const color = channelColorOf(channel.id);
            return (
              <div key={channel.id} style={{ position: 'relative' }}>
                <button
                  type="button"
                  data-f="1"
                  data-channel={channel.id}
                  data-focused={focused && !onStar ? '1' : '0'}
                  onClick={() => handlers.current.onOpen(channel.id)}
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: layout.favoriteTileHeight,
                    border: 0,
                    margin: 0,
                    borderRadius: 16,
                    cursor: 'pointer',
                    // Il colore del canale, come nella guida: è quel che permette di ritrovare una
                    // casella senza rileggerla.
                    background: color.background,
                    color: color.foreground,
                    font: `800 32px ${FONT_STACK}`,
                    letterSpacing: '-.02em',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 16px',
                    outline: focused && !onStar ? `4px solid ${colors.accent}` : 'none',
                    outlineOffset: 4,
                    /*
                      Il fuoco è un **contorno**, come nella guida — non un ingrandimento.

                      La casella che cresce sotto il cursore era un linguaggio suo: nel resto
                      dell'app il fuoco si dice col contorno, e due modi diversi di dire la stessa
                      cosa si notano proprio perché si alternano.
                    */
                  }}
                >
                  {/*
                    Il logo quando c'è, il nome quando non c'è — e non le due cose insieme.

                    Qui il nome era scritto sempre, anche per i canali che un marchio ce l'hanno: in
                    una schermata di preferiti, dove le caselle sono poche e scelte a mano, è proprio
                    il marchio la cosa che le fa riconoscere da tre metri. Una parete di testo
                    obbliga a **leggere** venti caselle per trovarne una.

                    Il ripiego è già scritto e vale per tutti e tre i casi — niente indirizzo,
                    indirizzo che carica, indirizzo morto — e l'ultimo non è un'eccezione: su stage
                    quasi metà dei canali non ha logo, e gli altri puntano a domini di terzi.
                    `ChannelLogo` disegna il nome sul colore del canale quando l'immagine non c'è o
                    non arriva, che è esattamente quel che si vedeva prima.

                    `contain` e non `cover`: qui il riquadro è largo e basso, e ritagliare
                    toglierebbe proprio la parte che rende un marchio riconoscibile.
                  */}
                  <ChannelLogo
                    name={channel.name}
                    logoUrl={channel.logoUrl}
                    colorSeed={channel.id}
                    fit="contain"
                    widthPx={layout.guideLogoWidth}
                    heightPx={layout.guideLogoHeight}
                    nameFontPx={17}
                  />
                  <span
                    style={{ position: 'absolute', left: 14, top: 12, font: `700 18px ${FONT_STACK}`, opacity: 0.85 }}
                  >
                    {channel.number}
                  </span>
                </button>

                {/*
                  La stella che toglie: stessa forma e stesso posto della riga della guida.

                  Piena, perché qui sono **tutti** preferiti: una stella vuota in una schermata di
                  preferiti direbbe il contrario di quel che è vero.
                */}
                <button
                  type="button"
                  data-f="1"
                  data-favorite-remove={channel.id}
                  data-focused={focused && onStar ? '1' : '0'}
                  aria-pressed
                  onClick={() => handlers.current.onRemove(channel.id)}
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: 12,
                    width: 40,
                    height: 40,
                    border: 0,
                    margin: 0,
                    padding: 0,
                    borderRadius: 20,
                    background: colors.background,
                    color: colors.accent,
                    cursor: 'pointer',
                    font: `800 22px ${FONT_STACK}`,
                    lineHeight: 1,
                    outline: focused && onStar ? `4px solid ${colors.accent}` : 'none',
                    outlineOffset: 3,
                    transition: 'var(--tr, transform .16s ease)',
                  }}
                >
                  ★
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
